import { defineStore } from 'pinia';
import { api } from 'boot/axios';
import { isDraftGuidesEnabled } from 'src/boot/interactive-guides';
import type {
  InteractiveGuideResponse,
  InteractiveGuideApiResponse,
  CachedGuide,
} from 'src/types/interactive-guides';

export const useInteractiveGuidesStore = defineStore('interactive-guides', {
  state: () => ({
    guides: new Map<string, CachedGuide>(),
    loading: new Set<string>(),
  }),

  getters: {
    /**
     * Get a cached guide by slug.
     */
    getGuide: (state) => {
      return (slug: string): InteractiveGuideResponse | null => {
        const cached = state.guides.get(slug);
        return cached?.guide ?? null;
      };
    },

    /**
     * Check if a guide is currently being fetched.
     */
    isLoading: (state) => {
      return (slug: string): boolean => {
        return state.loading.has(slug);
      };
    },

    /**
     * Whether to include draft guides (from sessionStorage).
     */
    includeDrafts: (): boolean => {
      return isDraftGuidesEnabled();
    },
  },

  actions: {
    /**
     * Fetch a guide by slug from the API.
     * Returns cached version if available.
     */
    async fetchGuide(slug: string): Promise<InteractiveGuideResponse | null> {
      // Return cached if available
      const cached = this.guides.get(slug);
      if (cached) {
        return cached.guide;
      }

      // Prevent duplicate requests
      if (this.loading.has(slug)) {
        // Wait for existing request (simple polling approach)
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (!this.loading.has(slug)) {
              clearInterval(checkInterval);
              resolve(this.guides.get(slug)?.guide ?? null);
            }
          }, 50);
        });
      }

      this.loading.add(slug);

      try {
        const params = this.includeDrafts ? { include_drafts: true } : {};
        const response = await api.get<InteractiveGuideApiResponse>(
          `/api/v1/interactive-guides/${slug}`,
          { params },
        );

        // Handle 204 No Content or empty response (guide is draft/unavailable)
        // We return a 204 as opposed to a 404 as we do not want any automated notifications
        // that the guide did not load. It will just fail silently by design as it's not vital to the page function.
        if (response.status === 204 || !response.data?.data) {
          this.guides.set(slug, { guide: null, fetchedAt: Date.now() });
          return null;
        }

        const guide = response.data.data;

        this.guides.set(slug, {
          guide,
          fetchedAt: Date.now(),
        });

        return guide;
      } catch (error) {
        // Flush from cache on error
        this.guides.delete(slug);
        console.warn(
          `[Interactive Guides] Failed to fetch guide: ${slug}`,
          error,
        );
        return null;
      } finally {
        this.loading.delete(slug);
      }
    },

    /**
     * Remove a specific guide from cache.
     */
    invalidateGuide(slug: string): void {
      this.guides.delete(slug);
    },

    /**
     * Clear all cached guides.
     * Called on logout.
     */
    clear(): void {
      this.guides.clear();
      this.loading.clear();
    },
  },
});
